<template>
  <div class="historial-tab">
    <section-title
      class="mt-4 mb-3"
      has-line
      title="Historial de Servicio"
      title-class="section-title-info"
    />

    <time-line class="historial-line mt-4">
      <time-line-item
        v-for="history of records"
        :key="
          `Proposal-${history.proposalRequestId}-ProposalHistory-${history.id}`
        "
        :icon="getIcon(history.trackingTypeId)"
      >
        <template #date>
          <span class="color-secondary-6 font-12">Fecha:</span>
          <p class="color-black font-16 font-weight-bold">
            {{
              $formatDate(history.createdDate, $templateDate.longDateWithHour)
            }}
          </p>
        </template>

        <div id="main-info" class="row">
          <div class="col-12 col-md-2 additional-evaluation ml-2 mt-1">
            <div class="color-gray-12 font-18 font-weight-bold">
              {{ history.trackingTypeName || 'N/A' }}
            </div>
            <custom-pill
              class="historial-tab__pill mb-3 mb-md-0"
              :has-border="false"
              icon="fa-xs fas fa-circle"
              :icon-class="
                `color-${getColorStatus(history.proposalRequestStatusId)}`
              "
              :text="history.statusName"
            />
          </div>

          <div class="col-12 col-md-3 mt-3 mt-md-0 mt-sm-3">
            <div class="color-secondary-6 font-14">
              Usuario:
            </div>
            <div class="color-black font-16 font-weight-normal">
              {{ history.fullName | capitalize }}
            </div>
          </div>

          <div class="col mt-3 mt-md-0 mt-sm-3">
            <div class="color-secondary-6 font-14">
              Descripción:
            </div>
            <div class="color-black font-16 font-weight-normal">
              {{ history.description }}
            </div>
          </div>
        </div>
      </time-line-item>
    </time-line>

    <div
      v-if="currentPage !== totalPages"
      class="d-flex justify-content-center align-items-center"
    >
      <action-button
        class="btn-see-more"
        i="fas fa-chevron-down"
        variant="outline-success rounded-pill"
        @click="loadMore"
      >
        Ver más historial
      </action-button>
    </div>
  </div>
</template>

<script>
import { TimeLine, TimeLineItem } from '@/components/common/timeline';
import { Colors } from '@/components/common';
import CustomPill from 'custom-pill';
import { getTrackingHistoryByProposalRequestId } from '@/modules/serviceProposalHistory/api.js';
import proposalTrackingTypes from '@/views/provider/proposalHistory/proposalTrackingTypes.js';
import SectionTitle from 'SectionTitle';
import alertMixin from 'utils/mixins/alert.mixin';
import { proposalRequestStatus } from '@/utils/constants/proposalRequestStatus.js';
export default {
  components: {
    TimeLine,
    TimeLineItem,
    SectionTitle,
    CustomPill,
  },
  mixins: [alertMixin],
  props: {
    proposalRequestId: {
      type: Number,
      default: () => 0,
    },
  },
  data: () => ({
    records: [],
    currentPage: 1,
    totalPages: 0,
  }),
  async created() {
    await this.initTrackingHistory();
  },
  methods: {
    async initTrackingHistory() {
      await this.getTrackingHistoryByProposalRequestPage(1).then((data) => {
        this.records = [...data];
      });
    },

    getTrackingHistoryByProposalRequestPage(pageIndex) {
      return getTrackingHistoryByProposalRequestId(this.proposalRequestId, {
        pageIndex,
        pageSize: 5,
      })
        .then((res) => {
          this.totalPages = res.data.pageCount;
          this.currentPage = res.data.currentPage;
          return [...res.data.data];
        })
        .catch((err) => {
          this.Toast(
            'Alerta',
            err.response.data.message || 'error',
            'error',
            5000
          );
          return [];
        });
    },
    async loadMore() {
      const nextPage = Number(this.currentPage) + 1;
      this.getTrackingHistoryByProposalRequestPage(nextPage).then((data) => {
        this.records = [...this.records, ...data];
      });
    },
    getIcon(trackingType) {
      let result = '';

      switch (trackingType) {
        case proposalTrackingTypes.PropuestaSometida:
          result = 'fas fa-file-export';
          break;
        case proposalTrackingTypes.CambioDeEstatus:
          result = 'fas fa-retweet';
          break;
        case proposalTrackingTypes.PropuestaAsignada:
          result = 'fas fa-user-check';
          break;
        case proposalTrackingTypes.AsignacionManual:
          result = 'fas fa-edit';
          break;
        case proposalTrackingTypes.RemocionDeAsignacion:
          result = 'fas fa-minus';
          break;
        case proposalTrackingTypes.AnulacionDeEvaluacion:
        case proposalTrackingTypes.ConflictoDeInteres:
          result = 'fas fa-file-excel';
          break;
        case proposalTrackingTypes.PropuestaEvaluada:
          result = 'fas fa-minus-square';
          break;
        case proposalTrackingTypes.PropuestaRecomendada:
          result = 'fas fa-server';
          break;
        case proposalTrackingTypes.PropuestaNoRecomendada:
        case proposalTrackingTypes.DocumentoAprobado:
        case proposalTrackingTypes.DenegacionDeDocumento:
        case proposalTrackingTypes.ContratoRegistradoOC:
          result = 'fas fa-check';
          break;
        case proposalTrackingTypes.DocumentoDeEnmiendaSometido:
          result = 'far fa-copy';
          break;
        case proposalTrackingTypes.CargadosLosDocumentosDeContrato:
          result = 'fas fa-file-upload';
          break;
        case proposalTrackingTypes.AprobacionOGP:
          result = 'fas fa-file-contract';
          break;
      }

      return result;
    },
    getColorStatus(proposalServiceStatusId, hex = false) {
      let color = hex ? '#D5B89A' : 'brown';

      if (
        [
          proposalRequestStatus.Evaluated,
          proposalRequestStatus.OGPApproval,
          proposalRequestStatus.SignedContract,
          proposalRequestStatus.RegisteredContractOC,
          proposalRequestStatus.InApprovalAndPOCreationProcess,
        ].includes(proposalServiceStatusId)
      ) {
        color = hex ? '#b2e3b6' : Colors.GREEN_LIGHT;
      }

      if (
        [
          proposalRequestStatus.EvaluationProcess,
          proposalRequestStatus.Submitted,
          proposalRequestStatus.PendingShippingOGP,
          proposalRequestStatus.OGPApprovalApplication,
          proposalRequestStatus.RecommendedCondition,
          proposalRequestStatus.SecretaryOffice,
          proposalRequestStatus.PreliminaryRecommended,
          proposalRequestStatus.RegistryComptrollerOffice,
        ].includes(proposalServiceStatusId)
      ) {
        color = hex ? '#F8EA80' : Colors.YELLOW;
      }

      if (
        [
          proposalRequestStatus.ReadyForSignature,
          proposalRequestStatus.NotRecommended,
          proposalRequestStatus.Returned,
          proposalRequestStatus.FundSeparation,
          proposalRequestStatus.RecommendedHiring,
        ].includes(proposalServiceStatusId)
      ) {
        color = hex ? '#DDB3D7' : Colors.VIOLET;
      }

      if (
        [
          proposalRequestStatus.Assigned,
          proposalRequestStatus.OfficeSecretaryForSignature,
          proposalRequestStatus.ContractOffice,
          proposalRequestStatus.Recommended,
          proposalRequestStatus.ContractDrafting,
        ].includes(proposalServiceStatusId)
      ) {
        color = hex ? '#FCC9C8' : Colors.PINK;
      }

      if (
        [
          proposalRequestStatus.SentOGP,
          proposalRequestStatus.SentToSecretaryOffice,
          proposalRequestStatus.Signed,
        ].includes(proposalServiceStatusId)
      ) {
        color = hex ? '#A3E5B3' : Colors.CELADON;
      }

      if (
        [
          proposalRequestStatus.InSigningProcessSAEE,
          proposalRequestStatus.InSigningProcessSupplier,
          proposalRequestStatus.InSigningProcessSecretary,
          proposalRequestStatus.InSigningProcessLegalDivision,
        ].includes(proposalServiceStatusId)
      ) {
        color = hex ? '#F8B184' : Colors.AUTUMN_MIST;
      }

      if (
        [proposalRequestStatus.PendingOGP].includes(proposalServiceStatusId)
      ) {
        color = hex ? '#FAE76F' : Colors.HONEY_LIGHT;
      }

      return color;
    },
  },
};
</script>

<style lang="scss" scoped>
@import '~@/shared/scss/config/fonts.scss';
@import '~@/shared/scss/config/colors.scss';

.historial-tab {
  .btn-see-more {
    width: 250px;
  }

  .historial-tab__pill {
    font-weight: normal;
    color: map-get($colors, 'black');
    font-size: 1rem;
    padding: 0;
    text-align: left;
  }
}
</style>
